/**
 * 🔧 FIXED: OTP Login Page with Consistent Type Parameters
 * 
 * This component demonstrates the CORRECT implementation of OTP authentication
 * with matching type parameters between send-otp and verify-otp requests.
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Tabs,
  Tab,
  Stack,
  Divider,
  Link,
  FormControlLabel,
  Switch
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { 
  login, 
  loginWithOTP, 
  verifyLoginOTP, 
  registerWithOTP, 
  verifyRegistrationOTP 
} from '@/store/thunks/authThunks';
import { Phone, Email, Security, Login as LoginIcon } from '@mui/icons-material';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`auth-tabpanel-${index}`}
      aria-labelledby={`auth-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export const OTPLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { loading, error } = useAppSelector((state) => state.auth);

  const [activeTab, setActiveTab] = useState(0);
  const [isRegistering, setIsRegistering] = useState(false);

  // Email/Password form state
  const [emailForm, setEmailForm] = useState({
    email: 'admin@polarride.com',
    password: 'admin123',
  });

  // OTP form state
  const [otpForm, setOtpForm] = useState({
    phone: '+12176938032',
    otp: '',
  });

  const [otpSent, setOtpSent] = useState(false);
  const [otpSuccess, setOtpSuccess] = useState<string | null>(null);
  const [sentOtpType, setSentOtpType] = useState<string | null>(null); // Track what type was sent

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setOtpSent(false);
    setOtpSuccess(null);
    setSentOtpType(null); // Clear type tracking on tab change
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!emailForm.email || !emailForm.password) {
      return;
    }

    try {
      // 🔧 FIX: Support both email and phone login
      const isEmail = emailForm.email.includes('@');

      await dispatch(login(emailForm.email, emailForm.password));
      navigate('/dashboard');
    } catch (err) {
      console.error('Login failed:', err);
    }
  };

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Enhanced validation and logging
    const phoneNumber = otpForm.phone?.trim();
    const otpType = isRegistering ? 'registration' : 'login';
    
    if (!phoneNumber) {
      return;
    }

    // 🔍 ENHANCED DEBUGGING: Log exact values being sent

    try {
      setOtpSuccess(null);
      
      if (isRegistering) {
        await dispatch(registerWithOTP(phoneNumber));
        setOtpSuccess('Registration OTP sent successfully! Check your phone.');
        setSentOtpType('registration'); // Track sent type
      } else {
        await dispatch(loginWithOTP(phoneNumber));
        setOtpSuccess('Login OTP sent successfully! Check your phone.');
        setSentOtpType('login'); // Track sent type
      }
      
      setOtpSent(true);
    } catch (err: any) {
      console.error('❌ Send OTP failed:', err);
      setOtpSuccess(null);
    }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Enhanced validation and logging
    const phoneNumber = otpForm.phone?.trim();
    const otpCode = otpForm.otp?.trim();
    const otpType = isRegistering ? 'registration' : 'login';
    
    if (!phoneNumber || !otpCode) {
      return;
    }

    // 🔍 ENHANCED DEBUGGING: Log exact values being sent
    
    // 🚨 CRITICAL: Check for type mismatch
    if (sentOtpType && sentOtpType !== otpType) {
      console.error('🚨 TYPE MISMATCH DETECTED!');
      console.error(`  Sent: "${sentOtpType}" -> Verifying: "${otpType}"`);
      alert(`🚨 TYPE MISMATCH: Sent "${sentOtpType}" but verifying with "${otpType}"`);
    }

    try {
      if (isRegistering) {
        await dispatch(verifyRegistrationOTP(phoneNumber, otpCode));
      } else {
        await dispatch(verifyLoginOTP(phoneNumber, otpCode));
      }
      
      // Navigate to dashboard on success
      navigate('/dashboard');
    } catch (err: any) {
      console.error('❌ Verify OTP failed:', err);
    }
  };

  const handleReset = () => {
    setOtpForm({ phone: '+12176938032', otp: '' });
    setOtpSent(false);
    setOtpSuccess(null);
    setSentOtpType(null); // Clear tracked type
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      bgcolor: 'background.default',
      p: 2
    }}>
      <Card sx={{ maxWidth: 500, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
              🚗 PolarRide
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              Welcome Back
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Choose your preferred login method
            </Typography>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Success Alert */}
          {otpSuccess && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {otpSuccess}
            </Alert>
          )}

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={activeTab} onChange={handleTabChange} centered>
              <Tab 
                icon={<LoginIcon />} 
                label="Email/Phone Login" 
                id="auth-tab-0"
                aria-controls="auth-tabpanel-0"
              />
              <Tab 
                icon={<Phone />} 
                label="OTP Login" 
                id="auth-tab-1"
                aria-controls="auth-tabpanel-1"
              />
            </Tabs>
          </Box>

          {/* Email/Password Login */}
          <TabPanel value={activeTab} index={0}>
            <Box component="form" onSubmit={handleEmailLogin}>
              <TextField
                fullWidth
                label="Email or Phone Number"
                type="text"
                value={emailForm.email}
                onChange={(e) => setEmailForm(prev => ({ ...prev, email: e.target.value }))}
                margin="normal"
                required
                disabled={loading}
                placeholder="admin@polarride.com or +12176938032"
                helperText="Enter your email address or phone number"
                InputProps={{
                  startAdornment: emailForm.email.includes('@') 
                    ? <Email sx={{ mr: 1, color: 'action.active' }} />
                    : <Phone sx={{ mr: 1, color: 'action.active' }} />
                }}
              />

              <TextField
                fullWidth
                label="Password"
                type="password"
                value={emailForm.password}
                onChange={(e) => setEmailForm(prev => ({ ...prev, password: e.target.value }))}
                margin="normal"
                required
                disabled={loading}
                InputProps={{
                  startAdornment: <Security sx={{ mr: 1, color: 'action.active' }} />
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <LoginIcon />}
                sx={{ mt: 3, mb: 2 }}
              >
                {loading ? 'Signing In...' : 'Sign In'}
              </Button>
            </Box>
          </TabPanel>

          {/* OTP Login */}
          <TabPanel value={activeTab} index={1}>
            {/* Registration Toggle */}
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isRegistering}
                    onChange={(e) => {
                      setIsRegistering(e.target.checked);
                      handleReset();
                    }}
                    disabled={loading}
                  />
                }
                label={isRegistering ? "New User Registration" : "Existing User Login"}
              />
            </Box>

            {!otpSent ? (
              <Box component="form" onSubmit={handleSendOTP}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  type="tel"
                  value={otpForm.phone}
                  onChange={(e) => setOtpForm(prev => ({ ...prev, phone: e.target.value }))}
                  margin="normal"
                  required
                  disabled={loading}
                  placeholder="+12176938032"
                  helperText="Enter your phone number with country code"
                  InputProps={{
                    startAdornment: <Phone sx={{ mr: 1, color: 'action.active' }} />
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading || !otpForm.phone.trim()}
                  startIcon={loading ? <CircularProgress size={20} /> : <Phone />}
                  sx={{ mt: 3 }}
                >
                  {loading ? 'Sending...' : `Send ${isRegistering ? 'Registration' : 'Login'} OTP`}
                </Button>
              </Box>
            ) : (
              <Box component="form" onSubmit={handleVerifyOTP}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  type="tel"
                  value={otpForm.phone}
                  disabled
                  margin="normal"
                  InputProps={{
                    startAdornment: <Phone sx={{ mr: 1, color: 'action.active' }} />
                  }}
                />

                <TextField
                  fullWidth
                  label="OTP Code"
                  type="text"
                  value={otpForm.otp}
                  onChange={(e) => setOtpForm(prev => ({ ...prev, otp: e.target.value }))}
                  margin="normal"
                  required
                  disabled={loading}
                  placeholder="Enter 4-digit code"
                  helperText="Check your SMS for the verification code"
                  inputProps={{ maxLength: 10 }}
                />

                <Stack spacing={2} sx={{ mt: 3 }}>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={loading || !otpForm.otp.trim()}
                    startIcon={loading ? <CircularProgress size={20} /> : <Security />}
                  >
                    {loading ? 'Verifying...' : `Verify & ${isRegistering ? 'Register' : 'Login'}`}
                  </Button>

                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleReset}
                    disabled={loading}
                  >
                    Use Different Number
                  </Button>
                </Stack>
              </Box>
            )}
          </TabPanel>

          <Divider sx={{ my: 3 }} />

          {/* Test Credentials Info */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              🧪 Test Credentials:
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              Email: admin@polarride.com / admin123<br />
              Phone: +12176938032 / admin123<br />
              OTP: +12176938032 (Use test OTP: 1100)
            </Typography>
            
            <Box sx={{ mt: 2 }}>
              <Link href="#" variant="body2" color="primary">
                Need help? Contact Support
              </Link>
            </Box>

            {/* 🔍 DEBUG: Test bypass endpoint button */}
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Button
                variant="outlined" 
                size="small"
                color="warning"
                onClick={async () => {
                  try {
                    const response = await fetch('https://polarride-api-17dr.vercel.app/api/v1/auth/bypass-otp', {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                      },
                      body: JSON.stringify({
                        phone: '+12176938032',
                        bypass_code: 'DEMO123'
                      }),
                    });
                    
                    const result = await response.json();
                    
                    if (response.ok) {
                      alert('✅ Bypass endpoint works!');
                    } else {
                      alert(`❌ Bypass failed: ${result.error || 'Unknown error'}`);
                    }
                  } catch (error) {
                    console.error('Bypass test error:', error);
                    alert(`❌ Bypass test error: ${error}`);
                  }
                }}
                sx={{ fontSize: '0.7rem' }}
              >
                🧪 Test Bypass Endpoint
              </Button>
            </Box>
          </Box>

          {/* Debug Info (Development Only) */}
          {import.meta.env.MODE === 'development' && (
            <Box sx={{ 
              mt: 3, 
              p: 2, 
              bgcolor: 'info.light', 
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'info.main'
            }}>
              <Typography variant="h6" gutterBottom>
                🔍 Debug Information:
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                Build Time: {new Date().toISOString()}<br />
                Mode: {isRegistering ? 'Registration' : 'Login'}<br />
                Current Type: {isRegistering ? 'registration' : 'login'}<br />
                Sent OTP Type: {sentOtpType || 'None'}<br />
                Type Match: {sentOtpType === (isRegistering ? 'registration' : 'login') ? 'Yes' : 'No'}<br />
                Phone: {otpForm.phone}<br />
                OTP Sent: {otpSent ? 'Yes' : 'No'}<br />
                Loading: {loading ? 'Yes' : 'No'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Check browser console for detailed OTP flow logs
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};